import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { TID } from "../constants/testIds";
import { ArrowRight, Music, Sparkles, Calendar, MapPin } from "lucide-react";
import { resolveImageUrl } from "../lib/url";
import Seo from "../components/Seo";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_performing-arts-ch/artifacts/3e18juzc_IMG_2143.jpeg";
const MUSIC_IMG = "https://customer-assets.emergentagent.com/job_performing-arts-ch/artifacts/5gr6ue2f_DSC1110.jpeg";
const DANCE_IMG = "https://customer-assets.emergentagent.com/job_performing-arts-ch/artifacts/jlyxd6w3_IMG_4865.jpeg";

const Home = () => {
  const { t } = useLang();
  const [workshops, setWorkshops] = useState([]);
  const [events, setEvents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [musicDepts, setMusicDepts] = useState([]);
  const [danceDepts, setDanceDepts] = useState([]);

  useEffect(() => {
    api.get("/public/workshops").then(r => setWorkshops(r.data.slice(0, 3)));
    api.get("/public/events").then(r => setEvents(r.data.slice(0, 3)));
    api.get("/public/teachers").then(r => setTeachers(r.data.slice(0, 4)));
    api.get("/public/gallery").then(r => setGallery(r.data.slice(0, 6)));
    api.get("/public/departments?category=music").then(r => setMusicDepts(r.data));
    api.get("/public/departments?category=dance").then(r => setDanceDepts(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Tamilische Musik, Tanz & Kultur in Wil SG"
        description="KK Academy – Schweizer Akademie für tamilische Musik und Tanz. Mirudangam-Unterricht, Bollywood, Kuthu, HipHop, Bharatanatyam Kurse, Workshops und KAAVIYAM-Event."
        path="/"
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={HERO_IMG} alt="Tamil classical performance" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/40 bg-primary/5 text-primary text-xs tracking-[0.2em] uppercase mb-8 animate-fade-up">
              <Sparkles className="w-3.5 h-3.5" /> {t("hero.eyebrow")}
            </div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6 text-white animate-fade-up delay-100">
              {t("hero.headline")}
            </h1>
            <p className="text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed animate-fade-up delay-200">
              {t("hero.sub")}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-up delay-300">
              <Link to="/anmeldung" className="px-7 py-3.5 rounded-full font-medium inline-flex items-center gap-2 btn-gold" data-testid={TID.heroEnrollBtn}>
                {t("cta.enrollNow")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/musikakademie" className="px-7 py-3.5 rounded-full font-medium btn-outline-gold" data-testid={TID.heroDiscoverBtn}>
                {t("cta.discover")}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-white/40 tracking-[0.3em] uppercase">scroll</div>
      </section>

      {/* ACADEMY OVERVIEW (bento) */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{t("home.academyOverview")}</div>
            <h2 className="font-serif-display text-4xl md:text-5xl max-w-2xl leading-tight">{t("home.academyIntro")}</h2>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-6">
          <Link to="/musikakademie" data-testid={TID.homeMusicCard} className="group col-span-12 md:col-span-7 relative h-[420px] rounded-xl overflow-hidden card-academy">
            <img src={MUSIC_IMG} alt="School of Music" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8">
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-2">01 / Musik</div>
              <h3 className="font-serif-display text-4xl mb-2 text-white">{t("home.musicTitle")}</h3>
              <div className="font-tamil text-[hsl(var(--brand-gold-soft))] text-lg -mt-1 mb-2">{t("home.musicTitleTamil")}</div>
              <p className="text-white/70 max-w-sm text-sm">{t("home.musicSub")}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-primary text-sm group-hover:gap-3 transition-all">
                {t("cta.readMore")} <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
          <Link to="/tanzakademie" data-testid={TID.homeDanceCard} className="group col-span-12 md:col-span-5 relative h-[420px] rounded-xl overflow-hidden card-academy">
            <img src={DANCE_IMG} alt="School of Dance" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8">
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-2">02 / Tanz</div>
              <h3 className="font-serif-display text-4xl mb-2 text-white">{t("home.danceTitle")}</h3>
              <div className="font-tamil text-[hsl(var(--brand-gold-soft))] text-lg -mt-1 mb-2">{t("home.danceTitleTamil")}</div>
              <p className="text-white/70 max-w-sm text-sm">{t("home.danceSub")}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-primary text-sm group-hover:gap-3 transition-all">
                {t("cta.readMore")} <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* MUSIC DEPARTMENTS TEASER */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3 flex items-center gap-2">
                <Music className="w-3.5 h-3.5" /> {t("home.musicTitle")}
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl">Fachbereiche der Musik</h2>
            </div>
            <Link to="/musikakademie" className="text-primary text-sm inline-flex items-center gap-2 hover:gap-3 transition-all">
              {t("cta.viewAll")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {musicDepts.slice(0, 4).map((d) => (
              <Link key={d.id} to={`/fachbereich/${d.slug}`} className={`card-academy rounded-lg overflow-hidden group relative ${d.featured ? "md:col-span-2 md:row-span-1" : ""}`} data-testid={`music-dept-${d.slug}`}>
                {d.featured && (
                  <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-primary text-primary-foreground px-2.5 py-1 rounded-full">
                    <Sparkles className="w-3 h-3" /> Hauptfokus
                  </div>
                )}
                <div className={`overflow-hidden ${d.featured ? "aspect-[16/10]" : "aspect-[4/5]"}`}>
                  <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <div className="font-serif-display text-xl">{d.name}</div>
                  <div className={`text-xs mt-1 ${d.status === "active" ? "text-primary" : "text-muted-foreground"}`}>
                    {d.status === "active" ? t("department.active") : t("department.comingSoon")}
                  </div>
                  {d.featured && d.levels && d.levels.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {d.levels.map(l => (
                        <span key={l} className="text-[9px] sm:text-[10px] uppercase tracking-[0.12em] border border-border rounded-full px-1.5 sm:px-2 py-0.5 text-muted-foreground whitespace-nowrap">{l}</span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DANCE DEPARTMENTS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> {t("home.danceTitle")}
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl">Tanzrichtungen</h2>
            </div>
            <Link to="/tanzakademie" className="text-primary text-sm inline-flex items-center gap-2 hover:gap-3 transition-all">
              {t("cta.viewAll")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {danceDepts.slice(0, 5).map((d) => (
              <Link key={d.id} to={`/fachbereich/${d.slug}`} className="card-academy rounded-lg overflow-hidden group relative" data-testid={`dance-dept-${d.slug}`}>
                {d.featured && (
                  <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-primary text-primary-foreground px-2.5 py-1 rounded-full">
                    Fokus
                  </div>
                )}
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <div className="font-serif-display text-lg">{d.name}</div>
                  {d.levels && d.levels.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {d.levels.map(l => (
                        <span key={l} className="text-[9px] sm:text-[10px] uppercase tracking-[0.12em] border border-border rounded-full px-1.5 sm:px-2 py-0.5 text-muted-foreground whitespace-nowrap">{l}</span>
                      ))}
                    </div>
                  )}
                  {d.status !== "active" && (
                    <div className="text-xs text-muted-foreground mt-2">{t("department.comingSoon")}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WORKSHOPS */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{t("nav.workshops")}</div>
              <h2 className="font-serif-display text-3xl md:text-4xl">{t("home.workshopsTitle")}</h2>
              <p className="text-muted-foreground mt-2 max-w-xl">{t("home.workshopsSub")}</p>
            </div>
            <Link to="/workshops" className="text-primary text-sm inline-flex items-center gap-2 hover:gap-3 transition-all">
              {t("cta.viewAll")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {workshops.map((w) => (
              <Link key={w.id} to={`/workshops`} className="card-academy rounded-lg overflow-hidden group">
                <div className="aspect-video overflow-hidden">
                  <img src={w.image} alt={w.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2 inline-flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />{w.date}</div>
                  <div className="font-serif-display text-xl mb-2">{w.title}</div>
                  <div className="text-sm text-muted-foreground line-clamp-2">{w.description}</div>
                  <div className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{w.location}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{t("nav.events")}</div>
              <h2 className="font-serif-display text-3xl md:text-4xl">{t("home.eventsTitle")}</h2>
              <p className="text-muted-foreground mt-2 max-w-xl">{t("home.eventsSub")}</p>
            </div>
            <Link to="/events" className="text-primary text-sm inline-flex items-center gap-2 hover:gap-3 transition-all">
              {t("cta.viewAll")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {events.map((e) => (
              <div key={e.id} className="card-academy rounded-lg overflow-hidden group">
                <div className="grid grid-cols-5">
                  <div className="col-span-2 aspect-square overflow-hidden">
                    <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="col-span-3 p-6 flex flex-col justify-center">
                    <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{e.date} · {e.time}</div>
                    <div className="font-serif-display text-xl mb-2">{e.title}</div>
                    <div className="text-sm text-muted-foreground line-clamp-2">{e.description}</div>
                    <div className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{e.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEACHERS */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{t("nav.teachers")}</div>
          <h2 className="font-serif-display text-3xl md:text-4xl mb-10">{t("home.teachersTitle")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {teachers.map((tch) => (
              <div key={tch.id} className="card-academy rounded-lg overflow-hidden">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={tch.photo} alt={tch.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="font-serif-display text-lg">{tch.name}</div>
                  <div className="text-xs text-primary mt-1">{tch.role}</div>
                  <div className="text-xs text-muted-foreground mt-1">{tch.department}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">{t("nav.gallery")}</div>
              <h2 className="font-serif-display text-3xl md:text-4xl">{t("home.galleryTitle")}</h2>
            </div>
            <Link to="/galerie" className="text-primary text-sm inline-flex items-center gap-2 hover:gap-3 transition-all">
              {t("cta.viewAll")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {gallery.map((g) => (
              <div key={g.id} className="overflow-hidden rounded-lg aspect-[4/3]">
                <img src={resolveImageUrl(g.url)} alt={g.title} loading="lazy" className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="gold-divider mx-auto mb-10 w-32" />
          <h2 className="font-serif-display text-5xl md:text-6xl leading-tight mb-6">{t("home.ctaTitle")}</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">{t("home.ctaSub")}</p>
          <Link to="/anmeldung" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium btn-gold" data-testid={TID.ctaEnrollBtn}>
            {t("cta.enrollNow")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
